/**
 * DRAWER HANDLE geometry and the HANDLES-REQUIRED take-off (ADR 0064).
 *
 * Two jobs, one module, because they must never disagree: WHERE a handle is
 * drawn on a drawer face, and HOW MANY handles the job needs. The elevation
 * asks the first question and the summary asks the second, but a drawer with
 * two pulls drawn on it that reports as one handle to purchasing is a defect
 * you only find at the loading dock. So both callers run the SAME
 * `handlesForFace()` — the count is `handlesForFace(...).length`, never a
 * second rule re-implemented beside the renderer.
 *
 * Pure — no DOM, no model imports — so every rule below is node-testable the
 * way `interiorMarks` and `solveInterior` are.
 *
 * The owner's rules, verbatim (2026-08-09):
 *
 *   - "typically pulls and knobs (other than tab pulls which are top mounted
 *     so are on the top edge of the drawer face) are centered both
 *     horizontally and vertically on the drawer face."
 *   - "for drawers >=32" wide, they require two pulls unless the pulls are at
 *     least 25% the width of the drawer face" — the pair sitting at the
 *     quarter points, "use quarter points but put that in the admin
 *     maintenance so it can be modified".
 *   - The face is "the section width plus 1/2" due to the 1/4" overlay on the
 *     left and right side panels".
 *   - The default is a "standard stainless bar handle", and "standard pulls
 *     are 96mm long".
 *
 * Every number in those sentences is a business rule, not a constant here —
 * see `DEFAULT_BUSINESS_RULES` in src/config/business-rules.js. The defaults
 * duplicated in this file exist only so the module is usable standalone (the
 * same convention `elevation-interior.js` uses for the rod diameter); the app
 * always passes the resolved rules through `opts`.
 */

import { mmToUnits } from './system-holes.js';

/**
 * How a handle TYPE mounts on the face. Modelled as DATA because the whole
 * placement rule keys off it: a tab pull is top-mounted and everything else is
 * centred, so the mounting is the type's defining property, not a special case
 * spelled `if (type === 'tab')` inside the renderer.
 *
 *   - `centered` — centred both horizontally and vertically (bar pulls, knobs).
 *   - `topEdge`  — centred horizontally, sitting ON the face's top edge (tab
 *                  pulls, which are screwed to the face's top rail).
 *
 * `draw` says what SHAPE the elevation puts there: a `bar` spans its length,
 * a `knob` is a point. It is a drawing hint, not geometry — the count and the
 * two-pull rule never look at it.
 */
export const HANDLE_MOUNTINGS = Object.freeze(['centered', 'topEdge']);

/** The catalog's fallback, used when the rules carry no `handle_types` list. */
export const DEFAULT_HANDLE_TYPES = Object.freeze([
  { key: 'bar', label: 'Bar pull', mounting: 'centered', draw: 'bar' },
  { key: 'knob', label: 'Knob', mounting: 'centered', draw: 'knob' },
  { key: 'tab', label: 'Tab pull', mounting: 'topEdge', draw: 'bar' },
]);

const DEFAULT_HANDLE_TYPE_KEY = 'bar';
/** "standard pulls are 96mm long" — OVERALL length (owner-confirmed). */
const DEFAULT_HANDLE_LENGTH_MM = 96;
/** Faces at least this wide take two pulls. Inclusive — see `handleCount`. */
const DEFAULT_TWO_PULL_MIN_FACE_WIDTH_UNITS = 32;
/** A pull at least this fraction of the face width exempts it from the pair. */
const DEFAULT_SINGLE_PULL_MIN_WIDTH_FRACTION = 0.25;
/** Where a PAIR sits across the face — the quarter points. */
const DEFAULT_PAIR_POSITIONS = Object.freeze([0.25, 0.75]);
/** The face overhangs the opening by this much at EACH side (¼" overlay). */
const DEFAULT_FACE_SIDE_OVERLAY_UNITS = 0.25;

const EPS = 1e-9;
const isPos = (v) => Number.isFinite(v) && v > 0;

/**
 * The resolved handle rules, with every default filled in. One place that
 * knows the fallbacks, so no caller has to repeat `opts.x > 0 ? opts.x : 3`.
 *
 * @param {object} [opts] the resolved business rules (or any subset of them).
 * @returns {{types:Array, defaultTypeKey:string, lengthMm:number,
 *   twoPullMinFaceWidthUnits:number, singlePullMinWidthFraction:number,
 *   pairPositions:number[], faceSideOverlayUnits:number}}
 */
export function handleRules(opts = {}) {
  const types = Array.isArray(opts.handleTypes) && opts.handleTypes.length
    ? opts.handleTypes.filter((t) => t && typeof t.key === 'string' && t.key)
    : DEFAULT_HANDLE_TYPES;
  const positions = Array.isArray(opts.handlePairPositions)
    && opts.handlePairPositions.length
    && opts.handlePairPositions.every((p) => Number.isFinite(p))
    ? opts.handlePairPositions.slice()
    : DEFAULT_PAIR_POSITIONS.slice();
  return {
    types: types.length ? types : DEFAULT_HANDLE_TYPES,
    defaultTypeKey: typeof opts.defaultHandleType === 'string' && opts.defaultHandleType
      ? opts.defaultHandleType : DEFAULT_HANDLE_TYPE_KEY,
    lengthMm: isPos(opts.defaultHandleLengthMm) ? opts.defaultHandleLengthMm : DEFAULT_HANDLE_LENGTH_MM,
    twoPullMinFaceWidthUnits: isPos(opts.handleTwoPullMinFaceWidthUnits)
      ? opts.handleTwoPullMinFaceWidthUnits : DEFAULT_TWO_PULL_MIN_FACE_WIDTH_UNITS,
    singlePullMinWidthFraction: isPos(opts.handleSinglePullMinWidthFraction)
      ? opts.handleSinglePullMinWidthFraction : DEFAULT_SINGLE_PULL_MIN_WIDTH_FRACTION,
    pairPositions: positions,
    faceSideOverlayUnits: Number.isFinite(opts.drawerFaceSideOverlayUnits) && opts.drawerFaceSideOverlayUnits >= 0
      ? opts.drawerFaceSideOverlayUnits : DEFAULT_FACE_SIDE_OVERLAY_UNITS,
  };
}

/**
 * One handle TYPE's metadata, by key. An unknown key falls back to the default
 * type rather than throwing: a sketch stored against a handle the admin later
 * deleted from the catalog must still draw and still count.
 */
export function handleType(typeKey, opts = {}) {
  const r = handleRules(opts);
  const found = r.types.find((t) => t.key === typeKey);
  if (found) return normalizeType(found);
  const fallback = r.types.find((t) => t.key === r.defaultTypeKey) ?? r.types[0] ?? DEFAULT_HANDLE_TYPES[0];
  return normalizeType(fallback);
}

function normalizeType(t) {
  return Object.freeze({
    key: t.key,
    label: typeof t.label === 'string' && t.label ? t.label : t.key,
    mounting: HANDLE_MOUNTINGS.includes(t.mounting) ? t.mounting : 'centered',
    draw: t.draw === 'knob' ? 'knob' : 'bar',
    // THE CATALOG SEAM (see `handleLengthUnits`): a type may carry its own
    // overall length in mm. Nothing sets this yet.
    ...(isPos(t.lengthMm) ? { lengthMm: t.lengthMm } : {}),
  });
}

/**
 * A handle's OVERALL length, in inches.
 *
 * **96 mm is the OVERALL length, not centre-to-centre** (owner-confirmed,
 * 2026-08-09: "overall length is correct"). The distinction was worth checking
 * because 96 mm is conventionally a CTC boring spec and a 96 mm CTC bar
 * typically measures ~128 mm end to end — so a reader who assumes the industry
 * convention would conclude this module is drawing pulls a third too short.
 * It is not; the rule value IS the overall length. ADR 0064.
 *
 * **CATALOG SEAM.** Handle length will eventually come from the accessory
 * catalog entry the designer actually selected, not from one global default.
 * That arrives here and nowhere else: a `lengthMm` on the resolved TYPE wins
 * over the global rule, so wiring the catalog in later means populating
 * `handle_types` (or passing a type object through) — no call site changes and
 * no second length rule. Deliberately NOT built now (owner deferred it).
 * CODE-CLAIM: absent "lengthMm" src/config/cv-pricing-data.js
 */
export function handleLengthUnits(typeKey, opts = {}) {
  const t = handleType(typeKey, opts);
  const mm = isPos(t.lengthMm) ? t.lengthMm : handleRules(opts).lengthMm;
  return mmToUnits(mm);
}

/**
 * A drawer FACE's width from the section OPENING it covers (owner: "the
 * section width plus 1/2" due to the 1/4" overlay on the left and right side
 * panels"). The face overhangs its opening onto the side panels, so it is
 * always wider than the hole it fills — which is why the elevation now draws
 * faces PROUD of the opening rather than flush inside it.
 *
 * The horizontal overlay is its own rule. Do NOT reach for
 * `drawer_overlay_units` (3/8"): that is the VERTICAL overlay onto the shelves
 * above and below a face, a different number for a different edge.
 */
export function drawerFaceWidthUnits(openingUnits, opts = {}) {
  const w = isPos(openingUnits) ? openingUnits : 0;
  if (!(w > 0)) return 0;
  return w + 2 * handleRules(opts).faceSideOverlayUnits;
}

/**
 * How many handles ONE drawer face takes.
 *
 * The owner's rule: a face at least `handle_two_pull_min_face_width_units`
 * (32") wide takes TWO pulls, **unless** the pull itself is at least
 * `handle_single_pull_min_width_fraction` (25%) of the face width — a bar long
 * enough to span the face reads and pulls like two.
 *
 * **The threshold is INCLUSIVE (`>=`): exactly 32" gets two pulls.** The owner
 * phrased it twice, slightly differently — "for drawers >=32" wide, they
 * require two pulls" and later "one pull only for <32"". Both readings agree
 * that 32" itself is a two-pull face; `>=` is implemented and the admin hint
 * says so on screen, so nobody "corrects" it to `>` from the second phrasing.
 *
 * SANITY CHECK, owner-CONFIRMED: with the standard 96 mm (3.78") bar the
 * exception can never fire at 32", because 25% of 32" is 8" — so every face
 * 32" or wider gets two pulls with standard hardware, which is exactly the
 * real-world behaviour the owner described. The exception exists for long
 * appliance-style bars (an 18" bar on a 36" face beats the 9" bar), and only
 * for those.
 *
 * @param {number} faceWidthUnits the FACE width (not the opening).
 * @param {number} lengthUnits the handle's overall length, in inches.
 */
export function handleCount(faceWidthUnits, lengthUnits, opts = {}) {
  const r = handleRules(opts);
  const w = isPos(faceWidthUnits) ? faceWidthUnits : 0;
  if (!(w > 0)) return 0;
  if (w + EPS < r.twoPullMinFaceWidthUnits) return 1;
  // Wide enough for a pair — unless the pull is long enough to stand alone.
  const spans = isPos(lengthUnits) && lengthUnits + EPS >= r.singlePullMinWidthFraction * w;
  return spans ? 1 : Math.max(r.pairPositions.length, 1);
}

/**
 * WHERE the handles go on one drawer face, and how many there are.
 *
 * Coordinates are FACE-LOCAL: `x` runs 0 → `faceWidthUnits` across the face,
 * and `centerFromTopUnits` is how far BELOW the face's top edge the handle's
 * centreline sits. The caller owns the face's position in the drawing, so this
 * module never needs to know the elevation datum — the same separation
 * `interiorMarks` keeps between marks and pixels.
 *
 *   - `centered` mounting → `centerFromTopUnits = faceHeight / 2`, i.e.
 *     centred vertically, and centred horizontally (one pull) or at the
 *     `handle_pair_positions` quarter points (two).
 *   - `topEdge` mounting (TAB PULLS) → `centerFromTopUnits = 0`: the handle is
 *     ON the top edge, because a tab pull is top-mounted. Horizontal placement
 *     is unchanged, so a 36" face with tab pulls still gets a pair at the
 *     quarter points.
 *
 * @param {{widthUnits:number, heightUnits:number, type?:string}} face the face's
 *   REAL width (already through `drawerFaceWidthUnits`), its height, and the
 *   handle type key.
 * @param {object} [opts] the resolved business rules.
 * @returns {Array<{x:number, centerFromTopUnits:number, lengthUnits:number,
 *   type:string, mounting:string, draw:string}>} left-to-right; `[]` for a
 *   degenerate face (nothing is bought for a face clamped to nothing).
 */
export function handlesForFace(face, opts = {}) {
  const w = isPos(face?.widthUnits) ? face.widthUnits : 0;
  const h = isPos(face?.heightUnits) ? face.heightUnits : 0;
  if (!(w > 0) || !(h > 0)) return [];
  const r = handleRules(opts);
  const t = handleType(face.type, opts);
  const lengthUnits = handleLengthUnits(face.type, opts);
  const n = handleCount(w, lengthUnits, opts);
  if (!(n > 0)) return [];
  // A tab pull rides the TOP EDGE; everything else is centred vertically.
  const centerFromTopUnits = t.mounting === 'topEdge' ? 0 : h / 2;
  const fractions = n === 1 ? [0.5] : r.pairPositions.slice(0, n);
  return fractions.map((f) => ({
    x: w * f,
    centerFromTopUnits,
    lengthUnits,
    type: t.key,
    mounting: t.mounting,
    draw: t.draw,
  }));
}
